import React, { useEffect, useState } from 'react';
import { getMyEntries, getGames } from '../services/api';
import { usePickState }            from '../hooks/usePickState';
import PickGroupList               from '../components/PickGroupList';
import TiebreakerCard              from '../components/TiebreakerCard';

function Countdown({ deadline }) {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        const calc = () => {
            const diff = new Date(deadline) - new Date();
            if (diff <= 0) { setTimeLeft('Deadline passed'); return; }
            const d = Math.floor(diff / 86400000);
            const h = Math.floor((diff % 86400000) / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setTimeLeft(d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`);
        };
        calc();
        const interval = setInterval(calc, 1000);
        return () => clearInterval(interval);
    }, [deadline]);

    const urgent = new Date(deadline) - new Date() < 3600000;
    return (
        <span className={'text-xs font-bold ' + (urgent ? 'text-red-400' : 'text-gray-400')}>
            {timeLeft}
        </span>
    );
}

function PickSubmission({ user }) {
    const [entries,      setEntries]      = useState([]);
    const [activeEntry,  setActiveEntry]  = useState(null);
    const [games,        setGames]        = useState([]);

    // `now` updates every 30 seconds so isPastDeadline re-evaluates
    // automatically when a deadline passes mid-session (e.g. the
    // participant has the page open as the countdown hits zero).
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(id);
    }, []);
    const [feedback,     setFeedback]     = useState('');
    const [feedbackType, setFeedbackType] = useState('success');
    const [loading,      setLoading]      = useState(true);

    const showFeedback = (msg, type = 'success') => {
        setFeedback(msg);
        setFeedbackType(type);
        if (type === 'success') setTimeout(() => setFeedback(''), 3000);
    };

    useEffect(() => {
        getMyEntries()
            .then(res => {
                setEntries(res.data);
                if (res.data.length > 0) setActiveEntry(res.data[0]);
            })
            .finally(() => setLoading(false));
    }, []);

    // Each entry can be in a different tournament, so re-fetch games
    // whenever the active entry changes. Clear games first so the hook
    // doesn't briefly render the previous tournament's groups before
    // the new fetch lands.
    useEffect(() => {
        if (!activeEntry) { setGames([]); return; }
        getGames(activeEntry.tournament_id, null).then(res => setGames(res.data));
    }, [activeEntry]);

    const {
        picks,
        gameMap,
        groups,
        activeGroup,
        setActiveGroup,
        collapsedRounds,
        toggleCollapse,
        saving,
        handlePick,
        counts,
        effectiveDeadline,
        distribution,
    } = usePickState({
        activeEntry,
        games,
        poolType:                    activeEntry?.pool_type,
        onFeedback:                  showFeedback,
        autoOpenFirstScheduledGroup: true,
        collapseSingleRoundGroups:   false,
    });

    const { pickedCount, totalInGroup, isGroupLocked } = counts;
    const totalScheduled  = totalInGroup;
    const isPastDeadline  = effectiveDeadline !== null && now > new Date(effectiveDeadline);

    // Identify the championship game (highest-numbered round's single
    // game) from the games payload the page already fetched. Reused
    // for both the lock state and the input labels in TiebreakerCard.
    const championshipGame = games.length > 0
        ? games.find(g => g.round_number === Math.max(...games.map(x => x.round_number)))
        : null;
    const championshipFinal = championshipGame?.status === 'final';

    // After a tiebreaker save, refresh both the active entry and its
    // row in `entries` so the inputs keep showing the saved values
    // across tab switches without a full re-fetch.
    const handleTiebreakerSaved = (savedRow) => {
        setActiveEntry(prev => prev && prev.entry_id === savedRow.id
            ? { ...prev,
                tiebreaker_team1_score: savedRow.tiebreaker_team1_score,
                tiebreaker_team2_score: savedRow.tiebreaker_team2_score }
            : prev);
        setEntries(prev => prev.map(e =>
            e.entry_id === savedRow.id
                ? { ...e,
                    tiebreaker_team1_score: savedRow.tiebreaker_team1_score,
                    tiebreaker_team2_score: savedRow.tiebreaker_team2_score }
                : e));
    };

    if (loading) {
        return <div className="text-gray-400 text-center py-12">Loading...</div>;
    }

    if (entries.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-12 text-center">
                <p className="text-gray-400">You are not enrolled in any pools.</p>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <h2 className="text-2xl font-black text-white mb-4 uppercase tracking-wide">
                Submit Picks
            </h2>

            {feedback && (
                <div className={'px-4 py-3 rounded-lg mb-4 text-sm font-medium flex items-center justify-between ' +
                    (feedbackType === 'success'
                        ? 'bg-green-900/50 border border-green-600 text-green-300'
                        : 'bg-red-900/50 border border-red-500 text-red-300')}>
                    <span>{feedback}</span>
                    <button onClick={() => setFeedback('')} className="ml-4 text-lg font-black opacity-60 hover:opacity-100">×</button>
                </div>
            )}

            <div className="sticky top-24 z-40 bg-gray-950 pb-2">
                <div className="flex gap-2 mb-3 flex-wrap">
                    {entries.map(entry => (
                        <button
                            key={entry.entry_id}
                            onClick={() => { setActiveEntry(entry); setActiveGroup(null); }}
                            className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                                (activeEntry && activeEntry.entry_id === entry.entry_id
                                    ? 'bg-[var(--org-accent,#f97316)] text-white'
                                    : 'bg-gray-800 text-gray-400 hover:text-white')}
                        >
                            {entry.pool_name}
                        </button>
                    ))}
                </div>

                {totalScheduled > 0 && (
                    <div className={'px-5 py-3 rounded-xl mb-3 border flex items-center justify-between ' +
                        (isPastDeadline
                            ? 'bg-gray-900 border-gray-700'
                            : pickedCount === totalScheduled
                                ? 'bg-green-900/30 border-green-700'
                                : 'bg-gray-900 border-gray-700')}>
                        <div>
                            <p className="text-white font-bold text-sm">
                                {pickedCount === totalScheduled
                                    ? 'All picks submitted!'
                                    : `${pickedCount} of ${totalScheduled} picks submitted`}
                            </p>
                            {isPastDeadline ? (
                                <div className="flex items-center gap-1 mt-0.5">
                                    <span className="text-xs font-bold text-red-400">🔒 Picks locked — deadline passed</span>
                                </div>
                            ) : effectiveDeadline && (
                                <div className="flex items-center gap-1 mt-0.5">
                                    <p className="text-gray-500 text-xs">Deadline:</p>
                                    <Countdown deadline={effectiveDeadline} />
                                </div>
                            )}
                        </div>
                        {!isPastDeadline && pickedCount === totalScheduled && (
                            <span className="text-green-400 text-2xl">✓</span>
                        )}
                        {isPastDeadline && (
                            <span className="text-red-400 text-xl">🔒</span>
                        )}
                    </div>
                )}

                {activeEntry?.use_tiebreaker && (
                    <TiebreakerCard
                        entry={activeEntry}
                        championshipGame={championshipGame}
                        championshipFinal={championshipFinal}
                        onSaved={handleTiebreakerSaved}
                        onFeedback={showFeedback}
                    />
                )}

                <div className="flex gap-1 border-b border-gray-800 overflow-x-auto">
                    {groups.map(group => {
                        const groupGames   = group.rounds.flatMap(r => r.games);
                        const hasScheduled = groupGames.some(g => g.status === 'scheduled');
                        const allFinal     = groupGames.every(g => g.status === 'final');
                        const isActive     = activeGroup && activeGroup.key === group.key;
                        return (
                            <button
                                key={group.key}
                                onClick={() => setActiveGroup(group)}
                                className={'px-4 py-2 text-sm font-bold rounded-t-lg transition-colors whitespace-nowrap ' +
                                    (isActive
                                        ? 'bg-gray-950 text-[var(--org-accent,#fb923c)] border-t border-x border-gray-800'
                                        : allFinal
                                            ? 'text-gray-600 hover:text-gray-500'
                                            : hasScheduled
                                                ? 'text-gray-300 hover:text-white'
                                                : 'text-gray-500 hover:text-gray-300')}
                            >
                                {group.label}
                                {allFinal && <span className="ml-1 text-xs text-gray-600">✓</span>}
                                {!allFinal && hasScheduled && groupGames.every(g => !g.team1_id && !g.team2_id) && (
                                    <span className="ml-1 text-xs">🔒</span>
                                )}
                                {!allFinal && hasScheduled && !groupGames.every(g => !g.team1_id && !g.team2_id) && (
                                    <span className="ml-1 text-xs text-[var(--org-accent,#fb923c)]">•</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {activeGroup && (
                <div className="mt-4">
                    <PickGroupList
                        activeGroup={activeGroup}
                        isGroupLocked={isGroupLocked}
                        isPastDeadline={isPastDeadline}
                        distribution={distribution}
                        picks={picks}
                        gameMap={gameMap}
                        saving={saving}
                        collapsedRounds={collapsedRounds}
                        onToggleCollapse={toggleCollapse}
                        onPick={handlePick}
                        collapsibleRule="multi-round-only"
                        regionDisplayNames={activeEntry?.region_display_names}
                    />
                </div>
            )}
        </div>
    );
}

export default PickSubmission;